import React, { Component, type ErrorInfo, type ReactNode } from 'react'


import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
}

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

class ErrorBoundaryClass extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // TODO: Log error to firebase
    console.error('ErrorBoundary caught error:', error, errorInfo)

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo)

    this.setState({
      error,
      errorInfo,
    })
  }

  handleReset = () => {
    this.setState({ hasError: false })
  }

  override render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error UI
      return (
        <div className='min-h-screen flex items-center justify-center p-4'>
          <Card className='w-full max-w-md'>
            <CardHeader>
              <CardTitle className='text-red-600'>Something went wrong</CardTitle>
              <CardDescription>
                An unexpected error occurred. The error has been reported and we&apos;re working to
                fix it.
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-4'>
              {import.meta.env.DEV && this.state.error && (
                <div className='bg-red-50 border border-red-200 rounded p-3'>
                  <p className='text-sm font-mono text-red-800'>{this.state.error.message}</p>
                  {this.state.errorInfo?.componentStack && (
                    <details className='mt-2'>
                      <summary className='text-sm text-red-600 cursor-pointer'>
                        Component Stack
                      </summary>
                      <pre className='text-xs text-red-700 mt-1 whitespace-pre-wrap'>
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              <div className='flex gap-2'>
                <Button onClick={this.handleReset} variant='outline'>
                  Try Again
                </Button>
                <Button onClick={() => window.location.reload()}>Reload Page</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}

// Simple error boundary wrapper using Sentry
export function ErrorBoundary({ children }: { children: ReactNode }) {
  return <ErrorBoundaryClass>{children}</ErrorBoundaryClass>
}

// Legacy class-based error boundary for compatibility
export { ErrorBoundaryClass }

// HOC for wrapping components with error boundary
export function withErrorBoundary<P extends object>(Component: React.ComponentType<P>) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary>
      <Component {...props} />
    </ErrorBoundary>
  )

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`

  return WrappedComponent
}
